import { Checkbox, message, Card, Input, Select } from 'antd';
import { useEffect, useState } from 'react';
import useAuthCheck from '../../redux/hooks/useAuthCheck';
import { visitReasonOptions } from '../../constant/global';
import '../../components/Appointment/AppointmentFlow.css';

const { TextArea } = Input;

// Diaspora-friendly: country code is picked, the patient types only their
// number. Default Zimbabwe. Codes sorted longest-first for prefix parsing.
const COUNTRY_CODES = [
  { code: '+263', label: '🇿🇼 Zimbabwe +263' },
  { code: '+27', label: '🇿🇦 South Africa +27' },
  { code: '+44', label: '🇬🇧 UK +44' },
  { code: '+1', label: '🇺🇸 USA / Canada +1' },
  { code: '+61', label: '🇦🇺 Australia +61' },
  { code: '+267', label: '🇧🇼 Botswana +267' },
  { code: '+260', label: '🇿🇲 Zambia +260' },
  { code: '+258', label: '🇲🇿 Mozambique +258' },
  { code: '+264', label: '🇳🇦 Namibia +264' },
  { code: '+353', label: '🇮🇪 Ireland +353' },
  { code: '+49', label: '🇩🇪 Germany +49' },
  { code: '+64', label: '🇳🇿 New Zealand +64' },
  { code: '+971', label: '🇦🇪 UAE +971' },
];

// Build the canonical international number, dropping any leading zero.
const composePhone = (code, national) => {
  const digits = (national || '').replace(/\D/g, '').replace(/^0+/, '');
  return digits ? `${code}${digits}` : '';
};

// Split an existing stored number back into (code, national) for display.
const parsePhone = (full) => {
  if (full && full.startsWith('+')) {
    const match = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length).find((c) => full.startsWith(c.code));
    if (match) return { code: match.code, national: full.slice(match.code.length) };
  }
  return { code: '+263', national: '' };
};

const PersonalInformation = ({ handleChange, selectValue, setPatientId = () => {} }) => {
  const { firstName, lastName, email, phone, reasonForVisit, description, address, problemType } = selectValue;
  const [checked, setChecked] = useState(false);
  const initialPhone = parsePhone(phone);
  const [countryCode, setCountryCode] = useState(initialPhone.code);
  const [national, setNational] = useState(initialPhone.national);
  const { data } = useAuthCheck();

  const onChange = (e) => setChecked(e.target.checked);

  const updatePhone = (code, nat) => {
    setCountryCode(code);
    setNational(nat);
    handleChange({ target: { name: 'phone', value: composePhone(code, nat) } });
  };

  const onFieldChange = (name, value) => {
    handleChange({ target: { name, value } });
  };

  useEffect(() => {
    if (checked) {
      if (data?.id) {
        setPatientId(data.id);
        message.success('Your account details have been linked.');
      } else {
        message.warning('Please log in to link your account.');
      }
    }
  }, [checked, data, setPatientId]);

  return (
    <div className="appointment-step appointment-step--patient">
      <p className="appointment-step__title">Patient information</p>
      <p className="appointment-step__subtitle">Please provide your details and reason for the visit.</p>

      <Card className="patient-form-card" size="small">
        <p className="patient-form-card__title">Account</p>
        <Checkbox checked={checked} onChange={onChange}>
          I already have an account — use my saved details
        </Checkbox>
      </Card>

      <Card className="patient-form-card" size="small">
        <p className="patient-form-card__title">Personal details</p>
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label">First name</label>
            <Input
              placeholder="First name"
              name="firstName"
              value={firstName || ''}
              onChange={(e) => handleChange(e)}
              size="large"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Last name</label>
            <Input
              placeholder="Last name"
              name="lastName"
              value={lastName || ''}
              onChange={(e) => handleChange(e)}
              size="large"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Email</label>
            <Input
              type="email"
              placeholder="email@example.com"
              name="email"
              value={email || ''}
              onChange={(e) => handleChange(e)}
              size="large"
            />
          </div>
          <div className="col-md-6">
            <label className="form-label">Phone (WhatsApp)</label>
            <Input
              addonBefore={
                <Select
                  value={countryCode}
                  onChange={(value) => updatePhone(value, national)}
                  options={COUNTRY_CODES.map((c) => ({ value: c.code, label: c.label }))}
                  style={{ width: 130 }}
                  popupMatchSelectWidth={220}
                />
              }
              placeholder="77 123 4567"
              value={national}
              onChange={(e) => updatePhone(countryCode, e.target.value)}
              size="large"
              inputMode="tel"
            />
            <small className="text-muted">Pick your country, then your number — no leading 0.</small>
          </div>
          <div className="col-12">
            <label className="form-label">Address (optional)</label>
            <Input
              placeholder="Street, city, state, ZIP"
              name="address"
              value={address || ''}
              onChange={(e) => handleChange(e)}
              size="large"
            />
          </div>
        </div>
      </Card>

      <Card className="patient-form-card" size="small">
        <p className="patient-form-card__title">Visit details</p>
        <div className="row g-3">
          <div className="col-12">
            <label className="form-label">Type of visit / Problem</label>
            <Select
              placeholder="Select type of visit"
              options={visitReasonOptions}
              value={problemType || undefined}
              onChange={(value) => onFieldChange('problemType', value)}
              allowClear
              style={{ width: '100%' }}
              size="large"
            />
          </div>
          <div className="col-12">
            <label className="form-label">Reason for visit *</label>
            <TextArea
              rows={3}
              placeholder="Describe your main reason for this appointment, symptoms, or concerns."
              name="reasonForVisit"
              value={reasonForVisit || ''}
              onChange={(e) => handleChange(e)}
              size="large"
            />
          </div>
          <div className="col-12">
            <label className="form-label">Additional notes (optional)</label>
            <TextArea
              rows={2}
              placeholder="Any other details you want the doctor to know (medications, allergies, etc.)."
              name="description"
              value={description || ''}
              onChange={(e) => handleChange(e)}
              size="large"
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PersonalInformation;
